import { format, Locale, parse } from 'date-fns';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronRight } from '../icons/chevron-right';
import { Layout } from './Layout';
import { MainTitle } from './typography/MainTitle';
import { Paragraph } from './typography/Paragraph';
import { Typography } from './typography/Typograhy';
import { enUS } from 'date-fns/locale';
import { useEffect, useState } from 'react';
import { KlartextEventConfig } from '@common/types/klartextEventConfig';
import { FrontendSpecificConfig } from '@configTypes/eventConfigs';

type Props = {
  klartextEventConfigs: KlartextEventConfig<FrontendSpecificConfig>[];
};

export const LandingPage = ({ klartextEventConfigs }: Props) => {
  const [searchParams] = useSearchParams();
  const [isTestmode, setIsTestmode] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (searchParams.get('test') === 'true') {
      setIsTestmode(true);
    }
  }, [searchParams]);

  const parseDate = (date: string) => {
    return parse(date, 'yyyy-MM-dd HH:mm:ss', new Date());
  };

  const sortSpeechByDateAsc = (
    a: KlartextEventConfig<FrontendSpecificConfig>,
    b: KlartextEventConfig<FrontendSpecificConfig>,
  ) => {
    const aDate = parseDate(a.eventStartTime);
    const bDate = parseDate(b.eventStartTime);

    return aDate.getTime() - bDate.getTime();
  };

  const getDateInLocale = (date: string, locale: Locale) => {
    return format(parseDate(date), "d MMMM', ' HH:mm", { locale: locale });
  };

  const getDayNameInLocale = (date: string, locale: Locale) => {
    return format(parseDate(date), 'EEEE', { locale: locale });
  };

  return (
    <Layout className={'mx-auto pt-8 lg:max-w-2xl'}>
      <Typography>
        <MainTitle>Welcome to Eidra Klartext</MainTitle>

        <Paragraph>
          Eidra Klartext does a live AI analysis of the presidential debates.
          <br />
          <br />
          On this page you can see when the debates are scheduled, follow the analysis in real-time or review the
          results and summaries of all the debates afterwards.
        </Paragraph>
      </Typography>
      <div className="grid grid-cols-1 gap-2 pt-[1.25rem]">
        {klartextEventConfigs &&
          klartextEventConfigs
            .filter(config => (isTestmode ? true : config.frontendSpecificConfig.public))
            .sort(sortSpeechByDateAsc)
            .map(speechConfig => {
              return (
                <button key={'btn_' + speechConfig.eventId} onClick={() => navigate('/' + speechConfig.eventId)}>
                  <div className={'grid grid-cols-5 gap-3 overflow-hidden rounded-lg border border-eidraDarkCream'}>
                    <div className={'col-span-4 flex px-2 py-4'}>
                      <div className={'col-span-1 shrink-0 grow-0 basis-9'}>
                        <img
                          src={speechConfig.frontendSpecificConfig.eventIcon}
                          alt={speechConfig.frontendSpecificConfig.eventSubtitle}
                          className={'inline-block w-8 max-w-full'}
                        />
                      </div>
                      <div className={'ml-2 space-y-2.5 text-left'}>
                        <div className={'flex flex-col gap-0.5'}>
                          <h2 className={'block truncate text-[1.125rem] font-medium leading-none'}>
                            {speechConfig.frontendSpecificConfig.eventTitle}
                          </h2>
                          <span className={'block text-[0.75rem] font-light leading-none'}>
                            {speechConfig.frontendSpecificConfig.eventSubtitle}
                          </span>
                        </div>
                        <Paragraph>
                          {speechConfig.transcriptionEndTime ? (
                            'Analysis finished'
                          ) : speechConfig.transcriptionStartTime ? (
                            'Live'
                          ) : (
                            <>
                              <span className={'capitalize'}>
                                {getDayNameInLocale(speechConfig.eventStartTime, enUS)}{' '}
                              </span>
                              {getDateInLocale(speechConfig.eventStartTime, enUS)}
                            </>
                          )}
                        </Paragraph>
                      </div>
                    </div>
                    <div className="col-span-1 flex items-center justify-center bg-eidraDarkCream">
                      <span className={'align'}>
                        <ChevronRight />
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
        <button key={'btn_almedalen'} onClick={() => window.open('https://klartext.eidra.com', '_blank')}>
          <div className={'grid grid-cols-5 gap-3 overflow-hidden rounded-lg border border-eidraDarkCream'}>
            <div className={'col-span-4 flex px-2 py-4'}>
              <div className={'col-span-1 shrink-0 grow-0 basis-9'}>
                <img src={'/favicon_almedalen.ico'} alt={'almedalen'} className={'inline-block w-8 max-w-full'} />
              </div>
              <div className={'ml-2 space-y-2.5 text-left'}>
                <div className={'flex flex-col gap-0.5'}>
                  <h2 className={'block truncate text-[1.125rem] font-medium leading-none'}>{'Almedalen 2024'}</h2>
                  <span className={'block text-[0.75rem] font-light leading-none'}>
                    {'Check out the Klartext analysis of the party leaders speeches at Almedalsveckan 2024'}
                  </span>
                </div>
                {/* <Paragraph>Analysis finished</Paragraph> */}
              </div>
            </div>
            <div className="col-span-1 flex items-center justify-center bg-eidraDarkCream">
              <span className={'align'}>
                <ChevronRight />
              </span>
            </div>
          </div>
        </button>
      </div>
    </Layout>
  );
};

export default LandingPage;
